import { convertTimeToMMSS } from '@src/utility/timeFormat';

import type { ClipFragment } from '@src/services/AIClipProducer';

/**
 * Assistant reply copy for a clip search (PLAT-11806).
 *
 * Pure string building, kept out of `ClipSearchService` so the phrasing can be
 * tested without standing up the producer. The reply's job is to answer the
 * question the user actually has after asking for a clip — *how long is it?* —
 * which the producer already knows: every `ClipFragment` carries its own
 * `duration`, so nothing has to be estimated here.
 */

/**
 * `00:24` — via the shared media-timecode formatter, since a candidate's
 * duration is exactly what `timeFormat.ts` owns (a length *within* a video).
 * Zero-padded minutes are that helper's house style; not worth a fork.
 */
export function formatCandidateDuration(seconds: number): string {
  return convertTimeToMMSS(Math.max(0, Math.round(seconds)));
}

/**
 * The length line: one duration when there is a single option, a range when
 * they differ, and a bare count when every option happens to be the same
 * length (where "0:24 to 0:24" would read like a mistake).
 */
export function describeDurations(candidates: ClipFragment[]): string {
  if (candidates.length === 0) return '';

  const durations = candidates.map(c => c.duration);
  const shortest = formatCandidateDuration(Math.min(...durations));
  const longest = formatCandidateDuration(Math.max(...durations));

  if (candidates.length === 1) return shortest;
  return shortest === longest ? shortest : `${shortest} to ${longest}`;
}

/**
 * The full assistant reply for a completed search.
 *
 * Deliberately short: the cards below it carry the speaker, timecode and text,
 * so repeating any of that here would just be noise above a list that already
 * says it.
 */
export function buildSearchReply(candidates: ClipFragment[]): string {
  if (candidates.length === 0) return '';

  const lengths = describeDurations(candidates);
  if (candidates.length === 1) {
    return `One option, ${lengths}. It's marked in the transcript.`;
  }
  return `${candidates.length} options, ${lengths}. They're marked in the transcript, best first.`;
}

/** The acknowledgement shown while the transcript is being read. */
export function buildSearchAcknowledgement(prompt: string): string {
  const trimmed = prompt.trim();
  const shortened =
    trimmed.length > 80 ? `${trimmed.slice(0, 77).trimEnd()}…` : trimmed;
  return `Reading the transcript for “${shortened}”…`;
}
